/**
 * IndexedDB Service for Dashboard
 * Handles all IndexedDB operations for dashboard data queries
 */

const DB_NAME = 'ShepherdDB';

// Store names
export const STORES = {
    DASHBOARD_CACHE: 'dashboard_cache',
    VERSION_TRACKING: 'version_tracking'
};

/**
 * Initialize IndexedDB stores for dashboard
 * @returns {Promise<IDBDatabase>}
 */
export const initDashboardStores = () => {
    return new Promise((resolve, reject) => {
        // Open without version to get current version
        const checkRequest = indexedDB.open(DB_NAME);
        
        checkRequest.onsuccess = (event) => {
            const db = event.target.result;
            const currentVersion = db.version;
            const needsUpgrade = !db.objectStoreNames.contains(STORES.DASHBOARD_CACHE) || 
                                 !db.objectStoreNames.contains(STORES.VERSION_TRACKING);
            db.close();
            
            if (!needsUpgrade) {
                // Stores already exist, just return connection
                resolve(db);
                return;
            }
            
            // Need to create stores, upgrade version
            const request = indexedDB.open(DB_NAME, currentVersion + 1);
            
            request.onerror = () => reject(request.error);
            request.onsuccess = () => resolve(request.result);
            
            request.onupgradeneeded = (event) => {
                const db = event.target.result;

                // Create dashboard_cache store if it doesn't exist
                if (!db.objectStoreNames.contains(STORES.DASHBOARD_CACHE)) {
                    const cacheStore = db.createObjectStore(STORES.DASHBOARD_CACHE, { 
                        keyPath: 'cache_key' 
                    });
                    cacheStore.createIndex('group_id', 'group_id', { unique: false });
                    cacheStore.createIndex('metric_type', 'metric_type', { unique: false });
                    cacheStore.createIndex('version', 'version', { unique: false });
                    cacheStore.createIndex('expires_at', 'expires_at', { unique: false });
                }

                // Create version_tracking store if it doesn't exist
                if (!db.objectStoreNames.contains(STORES.VERSION_TRACKING)) {
                    const versionStore = db.createObjectStore(STORES.VERSION_TRACKING, { 
                        keyPath: 'entity_key' 
                    });
                    versionStore.createIndex('entity_type', 'entity_type', { unique: false });
                    versionStore.createIndex('entity_id', 'entity_id', { unique: false });
                }
            };
        };
        
        checkRequest.onerror = () => {
            // Database doesn't exist yet, create with version 1
            const request = indexedDB.open(DB_NAME, 1);
            
            request.onerror = () => reject(request.error);
            request.onsuccess = () => resolve(request.result);
            
            request.onupgradeneeded = (event) => {
                const db = event.target.result;

                // Create dashboard_cache store if it doesn't exist
                if (!db.objectStoreNames.contains(STORES.DASHBOARD_CACHE)) {
                    const cacheStore = db.createObjectStore(STORES.DASHBOARD_CACHE, { 
                        keyPath: 'cache_key' 
                    });
                    cacheStore.createIndex('group_id', 'group_id', { unique: false });
                    cacheStore.createIndex('metric_type', 'metric_type', { unique: false });
                    cacheStore.createIndex('version', 'version', { unique: false });
                    cacheStore.createIndex('expires_at', 'expires_at', { unique: false });
                }

                // Create version_tracking store if it doesn't exist
                if (!db.objectStoreNames.contains(STORES.VERSION_TRACKING)) {
                    const versionStore = db.createObjectStore(STORES.VERSION_TRACKING, { 
                        keyPath: 'entity_key' 
                    });
                    versionStore.createIndex('entity_type', 'entity_type', { unique: false });
                    versionStore.createIndex('entity_id', 'entity_id', { unique: false });
                }
            };
        };
    });
};

/**
 * Get database connection (opens with current version)
 * @returns {Promise<IDBDatabase>}
 */
const getDB = () => {
    return new Promise((resolve, reject) => {
        const request = indexedDB.open(DB_NAME);
        request.onerror = () => reject(request.error);
        request.onsuccess = () => resolve(request.result);
    });
};

/**
 * Count total records in a database group
 * @param {number} groupId - Database group ID
 * @param {string} storeName - Name of the store to query
 * @returns {Promise<number>}
 */
export const countRecordsInGroup = async (groupId, storeName) => {
    try {
        const db = await getDB();
        const transaction = db.transaction([storeName], 'readonly');
        const store = transaction.objectStore(storeName);
        
        return new Promise((resolve, reject) => {
            const countRequest = store.count();
            countRequest.onsuccess = () => resolve(countRequest.result);
            countRequest.onerror = () => reject(countRequest.error);
        });
    } catch (error) {
        console.error('[IndexedDB] Error counting records:', error);
        return 0;
    }
};

/**
 * Get column breakdown (group by column with counts)
 * @param {string} storeName - Name of the store to query
 * @param {string} columnName - Column to group by
 * @returns {Promise<Array<{value: string, count: number, percentage: number}>>}
 */
export const getColumnBreakdown = async (storeName, columnName) => {
    try {
        const db = await getDB();
        const transaction = db.transaction([storeName], 'readonly');
        const store = transaction.objectStore(storeName);
        
        return new Promise((resolve, reject) => {
            const request = store.openCursor();
            const breakdown = new Map();
            let totalCount = 0;

            request.onsuccess = (event) => {
                const cursor = event.target.result;
                
                if (cursor) {
                    const value = cursor.value[columnName];
                    const key = value !== undefined && value !== null ? String(value) : 'N/A';
                    
                    breakdown.set(key, (breakdown.get(key) || 0) + 1);
                    totalCount++;
                    cursor.continue();
                } else {
                    // Convert to array with percentages
                    const result = Array.from(breakdown.entries()).map(([value, count]) => ({
                        value,
                        count,
                        percentage: totalCount > 0 ? (count / totalCount) * 100 : 0
                    }));
                    
                    // Sort by count descending
                    result.sort((a, b) => b.count - a.count);
                    resolve(result);
                }
            };

            request.onerror = () => reject(request.error);
        });
    } catch (error) {
        console.error('[IndexedDB] Error getting column breakdown:', error);
        return [];
    }
};

/**
 * Get daily growth for last N days
 * @param {string} storeName - Name of the store to query
 * @param {number} days - Number of days to look back (default: 30)
 * @param {string} dateField - Name of the date field (default: 'created_at')
 * @returns {Promise<Array<{date: string, count: number}>>}
 */
export const getDailyGrowth = async (storeName, days = 30, dateField = 'created_at') => {
    try {
        const db = await getDB();
        const transaction = db.transaction([storeName], 'readonly');
        const store = transaction.objectStore(storeName);
        
        const now = new Date();
        const startDate = new Date(now);
        startDate.setDate(startDate.getDate() - days);
        startDate.setHours(0, 0, 0, 0);
        
        return new Promise((resolve, reject) => {
            const request = store.openCursor();
            const dailyCounts = new Map();
            
            // Initialize all dates with 0
            for (let i = 0; i < days; i++) {
                const date = new Date(startDate);
                date.setDate(date.getDate() + i);
                const dateKey = date.toISOString().split('T')[0];
                dailyCounts.set(dateKey, 0);
            }

            request.onsuccess = (event) => {
                const cursor = event.target.result;
                
                if (cursor) {
                    const record = cursor.value;
                    const recordDate = new Date(record[dateField]);
                    
                    if (recordDate >= startDate && recordDate <= now) {
                        const dateKey = recordDate.toISOString().split('T')[0];
                        dailyCounts.set(dateKey, (dailyCounts.get(dateKey) || 0) + 1);
                    }
                    
                    cursor.continue();
                } else {
                    // Convert to array and sort by date
                    const result = Array.from(dailyCounts.entries())
                        .map(([date, count]) => ({ date, count }))
                        .sort((a, b) => a.date.localeCompare(b.date));
                    
                    resolve(result);
                }
            };

            request.onerror = () => reject(request.error);
        });
    } catch (error) {
        console.error('[IndexedDB] Error getting daily growth:', error);
        return [];
    }
};

/**
 * Get all records from a store (for advanced queries)
 * @param {string} storeName - Name of the store
 * @returns {Promise<Array>}
 */
export const getAllRecords = async (storeName) => {
    try {
        const db = await getDB();
        const transaction = db.transaction([storeName], 'readonly');
        const store = transaction.objectStore(storeName);
        
        return new Promise((resolve, reject) => {
            const request = store.getAll();
            request.onsuccess = () => resolve(request.result);
            request.onerror = () => reject(request.error);
        });
    } catch (error) {
        console.error('[IndexedDB] Error getting all records:', error);
        return [];
    }
};

/**
 * Get records in batches (for large datasets)
 * @param {string} storeName - Name of the store
 * @param {number} batchSize - Size of each batch
 * @param {Function} callback - Callback for each batch
 * @returns {Promise<void>}
 */
export const getRecordsInBatches = async (storeName, batchSize, callback) => {
    try {
        const db = await getDB();
        const transaction = db.transaction([storeName], 'readonly');
        const store = transaction.objectStore(storeName);
        
        return new Promise((resolve, reject) => {
            const request = store.openCursor();
            let batch = [];
            let count = 0;

            request.onsuccess = async (event) => {
                const cursor = event.target.result;
                
                if (cursor) {
                    batch.push(cursor.value);
                    count++;
                    
                    if (batch.length >= batchSize) {
                        await callback(batch, count);
                        batch = [];
                    }
                    
                    cursor.continue();
                } else {
                    // Process remaining batch
                    if (batch.length > 0) {
                        await callback(batch, count);
                    }
                    resolve();
                }
            };

            request.onerror = () => reject(request.error);
        });
    } catch (error) {
        console.error('[IndexedDB] Error getting records in batches:', error);
        throw error;
    }
};

/**
 * Clear expired cache entries
 * @returns {Promise<number>} Number of entries cleared
 */
export const clearExpiredCache = async () => {
    try {
        const db = await getDB();
        const transaction = db.transaction([STORES.DASHBOARD_CACHE], 'readwrite');
        const store = transaction.objectStore(STORES.DASHBOARD_CACHE);
        const index = store.index('expires_at');
        
        const now = Date.now();
        let deletedCount = 0;
        
        return new Promise((resolve, reject) => {
            const request = index.openCursor(IDBKeyRange.upperBound(now));
            
            request.onsuccess = (event) => {
                const cursor = event.target.result;
                
                if (cursor) {
                    cursor.delete();
                    deletedCount++;
                    cursor.continue();
                } else {
                    console.log(`[IndexedDB] Cleared ${deletedCount} expired cache entries`);
                    resolve(deletedCount);
                }
            };
            
            request.onerror = () => reject(request.error);
        });
    } catch (error) {
        console.error('[IndexedDB] Error clearing expired cache:', error);
        return 0;
    }
};
